import React from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import {formatDate} from "utils";
import {dayLabels, HygienistBackendJob, Requirements, Basis, Positions} from "components/EditJob";

import {
  Card,
  CardHeading,
  DescriptionText,
  InfoChip,
  InfoChipIconWrap,
  InfoChipLabel,
  InfoChipText,
  InfoChipValue,
  InfoChipsGrid,
} from '../HygienistJobDetails.styles';
import {
  AssignmentOutlined as AssignmentOutlinedIcon,
  EventOutlined as EventOutlinedIcon,
  SchoolOutlined as SchoolOutlinedIcon,
  ViewWeekOutlined as ViewWeekOutlinedIcon,
  WorkOutline as WorkOutlineIcon,
} from "@material-ui/icons";

interface JobInfoProps {
  job: HygienistBackendJob
}

dayjs.extend(utc);

// Compare server-side YYYY-MM-DD against the user's local day string. Using
// dayjs.utc() here causes a local-evening-in-NA posting for tomorrow to render
// as "Start Immediately" the moment the clock crosses into the next UTC day;
// using local dayjs() over the raw date string interprets it as local midnight
// and can flip the label a day early. String compare on YYYY-MM-DD avoids both.
const getStartDateDisplay = (startDate?: string): string => {
  if (!startDate) return '';
  const startYmd = String(startDate).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(startYmd)) return formatDate(startDate);
  const todayYmd = dayjs().format('YYYY-MM-DD');
  if (startYmd < todayYmd) {
    return 'Start Immediately';
  }
  return formatDate(startDate);
};

// T2-6045: Job Details + Job Description now render as separate cards. The
// six attributes are shown as compact icon chips in a 3-up grid (2-up on
// small screens) instead of the previous full-width row layout.
const JobInfo = ({job}: JobInfoProps) => {
  const role = (Positions.find(({ id }) => id === job.jobType) || {}).text;
  const contract = (Basis.find(({ id }) => id === job.basis) || {}).text;
  const startDate = job.startDate ? getStartDateDisplay(job.startDate) : '—';
  const daysNeeded = (job.daysOfWeek?.split(',').map((d) => +d) || []).reduce((acc, day, index) => {
    if (day === 1) {
      return `${acc.length ? `${acc} / ${dayLabels[index]}` : dayLabels[index]}`;
    }
    return acc;
  }, '') || '—';
  const minExperience = (Requirements.find(({ id }) => id === job.minYearsExp) || {}).text;

  const chips: { icon: React.ReactNode; label: string; value?: string }[] = [
    { icon: <WorkOutlineIcon />, label: 'Role', value: role },
    { icon: <AssignmentOutlinedIcon />, label: 'Contract', value: contract },
    { icon: <EventOutlinedIcon />, label: 'Start Date', value: startDate },
    { icon: <ViewWeekOutlinedIcon />, label: 'Days Needed', value: daysNeeded },
    { icon: <SchoolOutlinedIcon />, label: 'Minimum Experience', value: minExperience },
  ];

  return (
    <>
      <Card>
        <CardHeading>Job Details</CardHeading>
        <InfoChipsGrid>
          {chips.map((chip) => (
            <InfoChip key={chip.label}>
              <InfoChipIconWrap>{chip.icon}</InfoChipIconWrap>
              <InfoChipText>
                <InfoChipLabel>{chip.label}</InfoChipLabel>
                <InfoChipValue>{chip.value || '—'}</InfoChipValue>
              </InfoChipText>
            </InfoChip>
          ))}
        </InfoChipsGrid>
      </Card>

      <Card>
        <CardHeading>Job Description</CardHeading>
        <DescriptionText>{job.description}</DescriptionText>
      </Card>
    </>
  );
}

export default JobInfo;
